// src/lib/ga.ts
// GA4 pour SPA (propre & robuste):
// - Consent par défaut refusé (RGPD)
// - page_view via gtag + fallback "image beacon" (region1) si bloqué
// - session_start explicite (fiabilise les sessions)
// - user_properties: visitor_status (new/returning)
// - heartbeat (user_engagement) pour la présence en direct
// - helpers: setUserId(), setUserProperties(), markSessionStart(), trackFirstSeen()

declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
  }
}

export const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";
const REGION_ENDPOINT = "https://region1.google-analytics.com";
const DEBUG = process.env.NODE_ENV !== "production";

export interface GAParams {
  page_path?: string;
  page_location?: string;
  page_title?: string;
  [key: string]: any;
}

/* ---------------- Utils CID/SID (pour fallback beacon) ---------------- */
function getCid(): string {
  try {
    const key = "ga4_cid";
    let cid = localStorage.getItem(key);
    if (!cid) {
      cid = `${Math.random().toString(16).slice(2)}.${Date.now()}`;
      localStorage.setItem(key, cid);
    }
    return cid;
  } catch {
    return `${Math.random().toString(16).slice(2)}.${Date.now()}`;
  }
}

function getSid(): string {
  try {
    const key = "ga4_sid";
    let sid = sessionStorage.getItem(key);
    if (!sid) {
      sid = String(Math.floor(Date.now() / 1000));
      sessionStorage.setItem(key, sid);
    }
    return sid;
  } catch {
    return String(Math.floor(Date.now() / 1000));
  }
}

/* -------------- Fallback : image beacon (Measurement Protocol v2) -------------- */
function sendBeaconImage(eventName: string, params: GAParams = {}) {
  if (!GA_ID) return;

  const dl = encodeURIComponent(params.page_location || window.location.href);
  const dt = encodeURIComponent(params.page_title || document.title);
  const dp = encodeURIComponent(
    params.page_path || (window.location.pathname + window.location.search)
  );

  const url =
    `${REGION_ENDPOINT}/g/collect` +
    `?v=2` +
    `&tid=${encodeURIComponent(GA_ID)}` +
    `&cid=${encodeURIComponent(getCid())}` +
    `&sid=${encodeURIComponent(getSid())}` +
    `&en=${encodeURIComponent(eventName)}` +
    `&dl=${dl}` +
    `&dt=${dt}` +
    `&dp=${dp}`;

  const img = new Image();
  img.src = url;
}

/* ------------------------- Initialisation GA ------------------------- */
export function initGA() {
  if (!GA_ID) return;
  if (window.gtag) return;

  // dataLayer + shim gtag
  window.dataLayer = window.dataLayer || [];
  const gtag = (...args: any[]) => {
    window.dataLayer.push(args);
  };
  window.gtag = gtag;

  // Consent Mode v2 : refus par défaut
  gtag("consent", "default", {
    ad_storage: "denied",
    analytics_storage: "denied",
    functionality_storage: "denied",
    personalization_storage: "denied",
    security_storage: "granted",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });

  // Charge gtag.js
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  // Init GA4 (SPA : pas d’auto page_view)
  gtag("js", new Date());
  gtag("config", GA_ID, {
    send_page_view: false,
    anonymize_ip: true,
    ...(DEBUG ? { debug_mode: true } : {}),
  });
}

/* ----------------------------- API publique ----------------------------- */
export function pageview(path?: string) {
  const params: GAParams = {
    page_path: path ?? window.location.pathname + window.location.search,
    page_location: window.location.href,
    page_title: document.title,
    ...(DEBUG ? { debug_mode: true } : {}),
  };

  // 1) gtag normal
  window.gtag?.("event", "page_view", { ...params, transport_type: "image" });

  // 2) fallback image
  sendBeaconImage("page_view", params);
}

export function gaEvent(name: string, params: GAParams = {}) {
  const p: GAParams = { ...(params || {}), ...(DEBUG ? { debug_mode: true } : {}) };

  window.gtag?.("event", name, { ...p, transport_type: "image" });

  // Fallback image (encode les infos de page pour la MP v2)
  sendBeaconImage(name, {
    page_path: p.page_path,
    page_location: p.page_location,
    page_title: p.page_title,
  });
}

/** Consentement accepté (ta bannière doit appeler cette fonction) */
export function grantAnalyticsConsent() {
  window.gtag?.("consent", "update", {
    analytics_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
  });
  pageview(); // trace la page courante
}

/** Consentement refusé */
export function denyAnalyticsConsent() {
  window.gtag?.("consent", "update", {
    analytics_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
}

/** Lier les sessions à un utilisateur (meilleures stats d’"utilisateurs uniques") */
export function setUserId(userId: string | null) {
  if (!userId) {
    window.gtag?.("config", GA_ID, { user_id: undefined });
    return;
  }
  window.gtag?.("config", GA_ID, { user_id: userId });
}

/** Définir des user_properties (ex: user_type, visitor_status) */
export function setUserProperties(props: Record<string, any>) {
  if (!props || typeof props !== "object") return;
  window.gtag?.("set", "user_properties", props);
}

/** Marque le début de session (1 fois au premier chargement) */
export function markSessionStart() {
  try {
    const key = "ga4_session_started";
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  } catch {}
  window.gtag?.("event", "session_start", {});
  sendBeaconImage("session_start", {});
}

/** Marque première visite (new/returning) via user_properties */
export function trackFirstSeen() {
  try {
    const key = "ga4_first_seen_at";
    const had = localStorage.getItem(key);
    if (!had) {
      localStorage.setItem(key, String(Date.now()));
      setUserProperties({ visitor_status: "new" });
      gaEvent("first_seen", {
        page_path: window.location.pathname + window.location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    } else {
      setUserProperties({ visitor_status: "returning" });
    }
  } catch {
    setUserProperties({ visitor_status: "unknown" });
  }
}

/* ----------------------------- Heartbeat ----------------------------- */
/** Envoie périodiquement un "user_engagement" quand l'onglet est visible */
let hbTimer: number | null = null;

export function startHeartbeat(intervalMs = 15000) {
  stopHeartbeat();
  const tick = () => {
    if (document.visibilityState !== "visible") return;
    gaEvent("user_engagement", {
      engagement_time_msec: intervalMs,
      page_path: window.location.pathname + window.location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  };
  // première impulsion immédiate
  tick();
  hbTimer = window.setInterval(tick, intervalMs);
  const visHandler = () => tick();
  document.addEventListener("visibilitychange", visHandler);
  // @ts-ignore: garder une ref simple
  (startHeartbeat as any)._vh = visHandler;
}

export function stopHeartbeat() {
  if (hbTimer) {
    clearInterval(hbTimer);
    hbTimer = null;
  }
  // @ts-ignore ref handler
  const visHandler = (startHeartbeat as any)._vh as (this: Document, ev: Event) => any;
  if (visHandler) {
    document.removeEventListener("visibilitychange", visHandler);
    // @ts-ignore
    (startHeartbeat as any)._vh = null;
  }
}